/**
 * ReconciliationPage.tsx — Unmatched bank transaction reconciliation
 *
 * Purpose:
 *   Lists unmatched bank transactions from GET /admin/reconciliation/unmatched.
 *   Each row has an invoice_id input and a 'Match' button that calls
 *   POST /admin/reconciliation/match with {transactionId, invoiceId}.
 *   Falls back to an EmptyState placeholder when the reconciliation routes
 *   are not yet live (80.01 incomplete) — detected as a loading error.
 *
 * @module packages/billing-admin/src/pages/ReconciliationPage
 * @since 2026-06-03 (AT-50.03 — Revenue Dashboard & Enterprise Screens)
 */
import React, { useState } from 'react';
import DataTable, { type Column } from '../components/DataTable';
import {
  useUnmatchedTransactions,
  matchTransaction,
  type UnmatchedTransaction,
} from '../hooks/useAdminApi50';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import SlidePanel from '../components/SlidePanel';

/**
 * ReconciliationPage — manual match interface for unmatched transactions.
 *
 * @since 2026-06-03 (AT-50.03)
 */
export default function ReconciliationPage() {
  const { token } = useAdminAuth();
  const { data, isLoading, error } = useUnmatchedTransactions();

  /** Per-row invoice ID input values, keyed by transaction id. */
  const [invoiceInputs, setInvoiceInputs] = useState<Record<string, string>>({});
  /** Per-row matching-in-progress flags. */
  const [matchingIds, setMatchingIds] = useState<Set<string>>(new Set());
  /** Per-row match error messages. */
  const [matchErrors, setMatchErrors] = useState<Record<string, string>>({});
  /** Transaction detail panel */
  const [detailTx, setDetailTx] = useState<UnmatchedTransaction | null>(null);

  // If there was an error loading transactions, show Coming Soon placeholder
  const isComingSoon = error !== null;

  function handleInvoiceInput(transactionId: string, value: string) {
    setInvoiceInputs(prev => ({ ...prev, [transactionId]: value }));
  }

  async function handleMatch(transactionId: string) {
    const invoiceId = invoiceInputs[transactionId]?.trim();
    if (!invoiceId || !token) return;

    setMatchingIds(prev => new Set([...prev, transactionId]));
    setMatchErrors(prev => {
      const n = { ...prev };
      delete n[transactionId];
      return n;
    });

    try {
      await matchTransaction(transactionId, invoiceId, token);
      setInvoiceInputs(prev => {
        const n = { ...prev };
        delete n[transactionId];
        return n;
      });
    } catch (err) {
      setMatchErrors(prev => ({
        ...prev,
        [transactionId]: err instanceof Error ? err.message : 'Match failed',
      }));
    } finally {
      setMatchingIds(prev => {
        const n = new Set(prev);
        n.delete(transactionId);
        return n;
      });
    }
  }

  const columns: Column<UnmatchedTransaction>[] = [
    { key: 'transaction_date', header: 'Date', render: r => r.transaction_date },
    {
      key: 'amount',
      header: 'Amount',
      render: r => `$${r.amount.toLocaleString()}`,
    },
    { key: 'description', header: 'Description', render: r => r.description },
    { key: 'bank_reference', header: 'Bank Ref', render: r => r.bank_reference },
    {
      key: 'match',
      header: 'Match to Invoice',
      sortable: false,
      render: r => (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder="Invoice ID"
              value={invoiceInputs[r.id] ?? ''}
              onChange={e => handleInvoiceInput(r.id, e.target.value)}
              className="w-32 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={() => handleMatch(r.id)}
              disabled={matchingIds.has(r.id) || !invoiceInputs[r.id]?.trim()}
              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {matchingIds.has(r.id) ? 'Matching…' : 'Match'}
            </button>
          </div>
          {matchErrors[r.id] && (
            <p className="text-red-500 text-xs">{matchErrors[r.id]}</p>
          )}
        </div>
      ),
    },
  ];

  if (isComingSoon) {
    return (
      <AdminLayout title="Reconciliation">
        <div className="space-y-4">
          <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Operations</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Reconciliation queue</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              This workspace is where unmatched transactions should be resolved once the backend route is healthy.
            </p>
          </section>
          <div className="bg-white rounded-lg shadow p-12 flex flex-col items-center justify-center text-center">
            <p className="text-lg font-semibold text-gray-700">Reconciliation route unavailable</p>
            <p className="text-sm text-gray-500 mt-1">
              The reconciliation backend is not returning data right now.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Reconciliation">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Operations</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Reconciliation queue</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Match inbound bank transactions to payments so finance operations are not buried in raw logs or ad hoc database investigation.
          </p>
        </section>
        <DataTable
          columns={columns}
          rows={data ?? []}
          rowKey={r => r.id}
          loading={isLoading}
          emptyMessage="No unmatched transactions."
          onRowClick={(row) => setDetailTx(row)}
        />

        {/* Transaction detail slide-in */}
        <SlidePanel
          open={detailTx !== null}
          onClose={() => setDetailTx(null)}
          title="Transaction Detail"
          subtitle={detailTx?.bank_reference ?? undefined}
          size="sm"
        >
          {detailTx && (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Amount</p>
                <p className="mt-1.5 text-2xl font-bold text-slate-900">${detailTx.amount.toLocaleString()}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Date</p>
                <p className="mt-1.5 text-sm text-slate-700">{detailTx.transaction_date}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Description</p>
                <p className="mt-1.5 text-sm text-slate-700">{detailTx.description}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Bank Reference</p>
                <p className="mt-1.5 font-mono text-xs text-slate-600">{detailTx.bank_reference}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Transaction ID</p>
                <p className="mt-1.5 font-mono text-xs text-slate-600">{detailTx.id}</p>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
    </AdminLayout>
  );
}
